import { Configuration, LogLevel } from '@azure/msal-browser';
import { authority } from '../lib/runtimeConfig';

// Auth tenant: the B&A Accounting **Entra External ID (CIAM)** tenant. Story #15948 (B1) cut the
// client portal off the BettsAssoc workforce tenant, where portal users were B2B guests — clients
// are now local accounts in a dedicated external tenant, signing in with email OTP through an
// invite-only user flow. MSAL recognises `*.ciamlogin.com` as a CIAM authority natively
// (msal-browser >= 3.x), so no `knownAuthorities` entry is required.
//
// Phase 11H runtime-config parity with the staff portal: read from window.__BABOMRS_CONFIG__
// first (server.js injects it from the client App Service env vars per env), then fall back to
// import.meta.env.VITE_* (local dev / pipeline-baked).
//
// `||` not `??`: server.js sets these to "" when the App Service env var is unset; "" is
// functionally unset, so fall through to the next layer.
//
// There are deliberately NO hard-coded app-registration defaults any more. The previous defaults
// pointed at the retired workforce app reg + tenant, which meant a misconfigured environment
// silently authenticated against the wrong directory instead of failing visibly.
const runtime = window.__BABOMRS_CONFIG__ ?? {};
const clientId = runtime.clientId || import.meta.env.VITE_ENTRA_CLIENT_ID || '';
const redirectUri = runtime.redirectUri || import.meta.env.VITE_ENTRA_REDIRECT_URI || window.location.origin;

export const msalConfig: Configuration = {
  auth: {
    clientId,
    // Full authority URL, injected whole: a CIAM authority host is `<tenant>.ciamlogin.com` and
    // cannot be composed from a tenant id the way `login.microsoftonline.com/<tenantId>` could.
    authority,
    redirectUri,
    postLogoutRedirectUri: redirectUri,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      logLevel: import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error,
    },
  },
};

// Scopes: request access token for the Client Portal API.
//
// `redirectStartPage: '/portal'` — after sign-in, land the client IN the portal.
// This origin serves the public marketing site at `/` and the authenticated portal at
// `/portal/*`. MSAL defaults to returning the user to whichever page started the request, so a
// client who clicks "Client Portal" in the public header was signed in and then dropped back on
// the marketing site, with only a header link as the way in — reported from production
// 2026-08-27 ("I still got the website not even the portal"). The dev path already forces
// `/portal` (App.tsx LoginRedirect), so production was the odd one out; this makes them agree.
//
// Set here rather than at the three loginRedirect call sites (App.tsx x2, SiteHeader.tsx) so
// there is one source of truth. Honoured because `navigateToLoginRequestUrl` is left at its
// default of true — MSAL ignores this field when that option is false.
//
// `openid`, `profile`, `email` are requested EXPLICITLY, not left to MSAL's implicit behaviour.
// Per Microsoft's access-token claims reference: `oid` — *"to receive this claim for users use the
// `profile` scope"* — and `preferred_username` likewise; `email` needs the `email` scope; `upn` is
// v1-only. The API identifies a client by `oid` and falls back to the stored email, so without those
// claims BOTH lookups miss: `oid` is absent, and `JwtEmail` is null so the link-by-email fallback is
// skipped by its own IsNullOrWhiteSpace guard. The result is an intended 403 that reads as
// "pending activation" — which is exactly what production showed on 2026-08-31.
// Requesting them explicitly is harmless if MSAL already appends them, and fixes it if it does not.
export const loginRequest = {
  scopes: ['openid', 'profile', 'email', `api://${clientId}/access_as_user`],
  redirectStartPage: '/portal',
};

// Same claim requirement applies to silent renewal — see loginRequest above.
export const tokenRequest = {
  scopes: ['openid', 'profile', 'email', `api://${clientId}/access_as_user`],
};
